import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Components
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import EmailGeneratorPage from './pages/EmailGeneratorPage';
import InvoiceCreatorPage from './pages/InvoiceCreatorPage';
import ProtectionHubPage from './pages/ProtectionHubPage';
import WhatShouldISayPage from './pages/WhatShouldISayPage';
import PaymentSuccessPage, { PaymentCancelPage } from './pages/PaymentSuccessPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminReviewsPage from './pages/AdminReviewsPage';
import AccountSettingsPage from './pages/AccountSettingsPage';

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Determine if navbar should be shown
  const hideNavbarPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
  const showNavbar = !hideNavbarPaths.some(path => location.pathname.startsWith(path));

  return (
    <>
      {showNavbar && <Navbar />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/payment-cancel" element={<PaymentCancelPage />} />
        
        {/* Protected Routes - Require login */}
        <Route 
          path="/email-generator" 
          element={
            <ProtectedRoute>
              <EmailGeneratorPage />
            </ProtectedRoute>
          } 
        />
        {/* Redirect old URL */}
        <Route path="/letter-generator" element={<Navigate to="/email-generator" replace />} />
        <Route 
          path="/invoice-creator" 
          element={
            <ProtectedRoute>
              <InvoiceCreatorPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/what-should-i-say" 
          element={
            <ProtectedRoute>
              <WhatShouldISayPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/protection-hub" 
          element={
            <ProtectedRoute>
              <ProtectionHubPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <ProtectedRoute>
              <AdminUsersPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/reviews" 
          element={
            <ProtectedRoute>
              <AdminReviewsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/account/settings" 
          element={
            <ProtectedRoute>
              <AccountSettingsPage />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </>
  );
}

function ThemedToaster() {
  const { isDark } = useTheme();
  return (
    <Toaster 
      position="top-right" 
      theme={isDark ? 'dark' : 'light'}
      toastOptions={{
        style: {
          background: isDark ? '#121216' : '#FFFFFF',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          color: isDark ? '#fff' : '#111',
        },
      }}
    />
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <div className="App min-h-screen bg-[#050507]">
            <AppRouter />
            <ThemedToaster />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
